import { useEffect, useState } from 'react';
import { eventBus } from '@/infra/event-bus/EventBus';
import { useT } from '@/infra/i18n';
import { COLORS, FONT } from '@/presentation/theme/tokens';

type RefusedAction = 'feed' | 'play' | 'clean' | 'discipline' | 'heal';
type RefusedSeverity = 'tantrum' | 'mild';
type RefusedReason = 'cooldown' | 'not-needed' | 'tantrum';

interface FeedbackState {
  key: string;
  action?: RefusedAction;
  severity?: RefusedSeverity;
  reason?: RefusedReason;
}

const HIDE_AFTER_MS = 1800;

export function ActionFeedback() {
  const t = useT();
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const show = (feedback: FeedbackState): void => {
      setFeedback(feedback);
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setFeedback(null), HIDE_AFTER_MS);
    };
    const unsub = eventBus.on('creature.refused', (payload) => {
      const next: FeedbackState = { key: feedbackKey(payload), action: payload.action, severity: payload.severity };
      if (payload.reason) next.reason = payload.reason;
      show(next);
    });
    const unsubs = [
      unsub,
      eventBus.on('creature.fed', () => show({ key: 'feedback.success.feed' })),
      eventBus.on('creature.played', () => show({ key: 'feedback.success.play' })),
      eventBus.on('creature.cleaned', () => show({ key: 'feedback.success.clean' })),
      eventBus.on('creature.healed', () => show({ key: 'feedback.success.heal' })),
      eventBus.on('creature.disciplined', () => show({ key: 'feedback.success.discipline' })),
      eventBus.on('creature.poop-spawned', () => show({ key: 'feedback.need.clean' })),
      eventBus.on('creature.hungry', () => show({ key: 'feedback.need.feed' })),
      eventBus.on('creature.unhappy', () => show({ key: 'feedback.need.play' })),
      eventBus.on('creature.sick-start', () => show({ key: 'feedback.need.heal' })),
    ];
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      unsubs.forEach((fn) => fn());
    };
  }, []);

  if (!feedback) return null;

  return (
    <>
      <div role="status" style={bubble}>
        {t(feedback.key)}
      </div>
      <style>{`
        @keyframes actionFeedbackPop {
          0% { transform: translateX(-50%) translateY(6px) scale(0.94); opacity: 0; }
          100% { transform: translateX(-50%) translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
    </>
  );
}

function feedbackKey(feedback: { action: RefusedAction; severity: RefusedSeverity; reason?: RefusedReason }): string {
  if (feedback.reason === 'cooldown') return `feedback.refused.${feedback.action}.cooldown`;
  if (feedback.reason === 'not-needed') return `feedback.refused.${feedback.action}.notNeeded`;
  if (feedback.severity === 'tantrum') return 'feedback.refused.tantrum';
  return `feedback.refused.${feedback.action}`;
}

const bubble: React.CSSProperties = {
  position: 'fixed',
  left: '50%',
  bottom: 104,
  transform: 'translateX(-50%)',
  zIndex: 96,
  maxWidth: 'min(420px, calc(100vw - 28px))',
  padding: '8px 12px',
  borderRadius: 10,
  border: '1px solid rgba(255, 226, 188, 0.62)',
  background: 'rgba(8, 10, 22, 0.9)',
  color: COLORS.accent,
  fontFamily: FONT.family,
  fontSize: FONT.sizeSm,
  fontWeight: 700,
  textAlign: 'center',
  textShadow: '0 1px 0 rgba(0,0,0,0.85)',
  boxShadow: '0 8px 20px rgba(0,0,0,0.24)',
  pointerEvents: 'none',
  animation: 'actionFeedbackPop 160ms steps(2)',
};
